import React, { useState, useEffect } from 'react';
import { Booking, Expense, Insight, ServiceType } from '../types';
import { 
  getBackupStatus, 
  snoozeBackupReminder, 
  triggerQuickJSONBackup,
  BackupStatus 
} from '../utils/backupManager';
import { 
  ShieldAlert, 
  Download, 
  Clock, 
  X, 
  CheckCircle2, 
  ArrowRight, 
  HardDriveDownload,
  Database
} from 'lucide-react';

interface BackupReminderToastProps {
  bookings: Booking[];
  expenses: Expense[];
  insights: Insight[];
  streams: ServiceType[];
  onOpenExportCenter: () => void;
  showToast: (msg: string, type?: 'success' | 'info') => void;
}

export function BackupReminderToast({
  bookings,
  expenses,
  insights,
  streams,
  onOpenExportCenter,
  showToast
}: BackupReminderToastProps) {
  const [status, setStatus] = useState<BackupStatus>(() => getBackupStatus(7));
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);

  // Sync state on events or interval
  useEffect(() => {
    const checkStatus = () => {
      setStatus(getBackupStatus(7));
    };

    checkStatus();
    window.addEventListener('solostack_backup_updated', checkStatus);
    const interval = setInterval(checkStatus, 30000); // Re-check every 30s

    return () => {
      window.removeEventListener('solostack_backup_updated', checkStatus);
      clearInterval(interval);
    };
  }, []);

  if (!status.isOverdue || status.isSnoozed || dismissedThisSession) {
    return null;
  }

  const handleBackupNow = () => {
    setIsBackingUp(true);
    setTimeout(() => {
      triggerQuickJSONBackup(bookings, expenses, insights, streams);
      setIsBackingUp(false);
      showToast('🛡️ Full workspace backup downloaded! Data safety timestamp updated.');
    }, 400);
  };

  const handleSnooze = () => {
    snoozeBackupReminder();
    setDismissedThisSession(true);
    showToast('⏰ Backup reminder snoozed for 24 hours.', 'info');
  };

  const handleDismiss = () => {
    setDismissedThisSession(true);
  };

  const daysText = status.daysSinceLastBackup === 0
    ? 'over 7 days'
    : `${status.daysSinceLastBackup} days`;

  return (
    <div className="fixed bottom-5 right-5 z-50 max-w-md w-[calc(100vw-2.5rem)] animate-in fade-in slide-in-from-bottom-5 duration-300">
      <div className="bg-[#0C0E0F] border-2 border-amber-500/40 hover:border-amber-500/70 rounded-2xl p-5 shadow-2xl shadow-amber-500/10 backdrop-blur-xl relative overflow-hidden">
        {/* Glow accent decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-rose-500/5 rounded-full blur-xl pointer-events-none" />

        <div className="relative z-10 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0 shadow-inner">
                <ShieldAlert size={22} className="animate-pulse" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-extrabold text-white tracking-tight">
                    Financial Backup Recommended
                  </h4>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-md shrink-0">
                    OVERDUE ({daysText})
                  </span>
                </div>
                <p className="text-[11px] text-gray-400 font-medium">
                  Safeguard your ledger, invoices & expense records
                </p>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-[#151819] rounded-lg transition-colors cursor-pointer shrink-0"
              title="Dismiss for current session"
            >
              <X size={16} />
            </button>
          </div>

          {/* Description */}
          <div className="p-3 bg-[#151819] border border-[#2A2F31] rounded-xl text-xs text-gray-300 space-y-1.5">
            <p className="leading-relaxed">
              You haven't backed up or exported your workspace data in <strong className="text-amber-400 font-bold">{daysText}</strong>. Regular backups protect your business records against browser cache clears or lost sessions.
            </p>
            <div className="flex items-center gap-3 text-[10px] text-gray-400 pt-1 border-t border-[#2A2F31]">
              <span className="flex items-center gap-1 text-emerald-400 font-semibold">
                <Database size={11} /> {bookings.length} Bookings
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1 text-rose-400 font-semibold">
                {expenses.length} Expenses
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1 text-teal-400 font-semibold">
                {insights.length} J-Lens Entries
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
            <button
              onClick={handleBackupNow}
              disabled={isBackingUp}
              className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50 shrink-0"
            >
              {isBackingUp ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  <span>Preparing JSON...</span>
                </>
              ) : (
                <>
                  <HardDriveDownload size={15} />
                  <span>Backup Now (JSON)</span>
                </>
              )}
            </button>

            <button
              onClick={() => {
                onOpenExportCenter();
                handleDismiss();
              }}
              className="px-3.5 py-2.5 bg-[#151819] hover:bg-[#1F2426] border border-[#2A2F31] hover:border-gray-500 text-gray-200 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
            >
              <span>Export Center</span>
              <ArrowRight size={13} className="text-gray-400" />
            </button>

            <button
              onClick={handleSnooze}
              className="px-3 py-2.5 bg-[#0C0E0F] hover:bg-[#151819] text-gray-400 hover:text-gray-200 text-xs font-semibold rounded-xl transition-colors flex items-center justify-center gap-1 cursor-pointer shrink-0"
              title="Remind me again in 24 hours"
            >
              <Clock size={13} />
              <span>Remind Later</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
